import request from '../utils/request';
import { clientbookApi } from '../core/clientbook-api';

const parseFilenameFromContentDisposition = (header, fallback) => {
  if (!header) return fallback;
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      // fall through
    }
  }
  const quotedMatch = /filename="([^"]+)"/i.exec(header);
  if (quotedMatch) return quotedMatch[1];
  const bareMatch = /filename=([^;]+)/i.exec(header);
  if (bareMatch) return bareMatch[1].trim();
  return fallback;
};

const getRolexInquiryExport = async (path, query, fallbackFilename) => {
  const response = await request({
    url: path,
    method: 'GET',
    params: query,
    responseType: 'blob',
  });
  return {
    blob: response.data,
    filename: parseFilenameFromContentDisposition(response.headers?.['content-disposition'], fallbackFilename),
  };
};

export const exportRolexInquiryDetail = (query = {}) =>
  getRolexInquiryExport('/rolex-inquiries/export/detail', query, 'rolex-inquiries-detail.csv');

export const exportRolexInquirySummary = (query = {}) =>
  getRolexInquiryExport('/rolex-inquiries/export/summary', query, 'rolex-inquiries-summary.zip');

export const getVendorById = vendorId => {
  return request({
    url: `/vendors/${vendorId}`,
    method: 'GET',
  });
};

export const getVendors = ({ params = {} }) => {
  return request({
    url: '/vendors/getVendors',
    method: 'GET',
    params,
  });
};

export const getVendorItems = (vendorId, { params = {} }) => {
  return request({
    url: `/vendors/${vendorId}/items`,
    method: 'GET',
    params,
  });
};

export const getVendorItemById = (vendorId, itemId) => {
  return request({
    url: `/vendors/${vendorId}/items/${itemId}`,
    method: 'GET',
  });
};

export const getVendorItem = itemId => {
  return request({
    url: `/vendors/item/${itemId}`,
    method: 'GET',
  });
};

export const addVendorItemToClientCollection = (clientId, collectionId, data) => {
  return request({
    url: `/clients/${clientId}/collections/${collectionId}/vendorItems`,
    method: 'POST',
    data,
  });
};

export const trackVendorEvent = data => {
  return request({
    url: '/vendors/events',
    method: 'POST',
    data,
  });
};

export const searchVendorItems = ({
  vendorIds,
  query = '',
  offset = 0,
  limit = 20,
  collections,
  families,
  materials,
}) => {
  const body = { vendorIds, searchTerm: query, offset, limit, filters: { archived: false } };
  if (collections?.length > 0) body.collections = collections;
  if (families?.length > 0) body.families = families;
  if (materials?.length > 0) body.materials = materials;
  return clientbookApi.post({
    url: '/searches/vendors/items',
    data: body,
  });
};

export const getVendorItemCollections = vendorIds => {
  return clientbookApi.post({
    url: '/searches/vendors/items/collections',
    data: { vendorIds },
  });
};

export const getVendorItemFamilies = vendorIds => {
  return clientbookApi.post({
    url: '/searches/vendors/items/families',
    data: { vendorIds },
  });
};

export const getVendorItemMaterials = vendorIds => {
  return clientbookApi.post({
    url: '/searches/vendors/items/materials',
    data: { vendorIds },
  });
};

// --- Inquiry API ---

export const listInquiries = (query = {}) => {
  return clientbookApi.get({
    url: '/inquiries',
    query,
    loading: false,
  });
};

export const getInquiryById = id => {
  return clientbookApi.get({
    url: `/inquiries/${id}`,
    loading: false,
  });
};

export const createInquiry = data => {
  return clientbookApi.post({
    url: '/inquiries',
    data,
    loading: false,
  });
};

export const updateInquiry = (id, data) => {
  return clientbookApi.put({
    url: `/inquiries/${id}`,
    data,
    loading: false,
  });
};

export const deleteInquiry = id => {
  return clientbookApi.delete({
    url: `/inquiries/${id}`,
    loading: false,
  });
};

export const getInquiriesByClient = clientId => {
  return clientbookApi.get({
    url: `/inquiries/clients/${clientId}`,
    loading: false,
  });
};

export const getInquiriesByVendorItem = vendorItemId => {
  return clientbookApi.get({
    url: `/inquiries/vendor-items/${vendorItemId}`,
    loading: false,
  });
};
